"use client";

import { Button, cn } from "@devhop/ui";
import { ArrowRightIcon, Loader2Icon } from "lucide-react";
import type { WorkshopJobStatusDTO } from "../../domain/contracts";
import {
  WORKSHOP_STATUS_LABELS,
  workshopPrimaryCtaLabel,
} from "../lib/workshop-labels";
import {
  workshopStatusActionIcon,
  workshopStatusPrimaryButtonClass,
  workshopStatusSecondaryButtonClass,
} from "./workshop-status-icons";

export function WorkshopStatusPrimaryButton({
  to,
  from,
  pending,
  onClick,
  className,
}: {
  to: WorkshopJobStatusDTO;
  from: WorkshopJobStatusDTO;
  pending?: boolean;
  onClick: () => void;
  className?: string;
}) {
  const Icon = workshopStatusActionIcon(to, from);
  const isCancel = to === "cancelled";

  return (
    <Button
      type="button"
      size="lg"
      variant={isCancel ? "destructive" : "default"}
      className={cn(
        "h-14 w-full gap-2.5 font-semibold text-base touch-manipulation",
        !isCancel && workshopStatusPrimaryButtonClass(to),
        className,
      )}
      disabled={pending}
      onClick={onClick}
    >
      {pending ? (
        <Loader2Icon className="size-5 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Icon className="size-5 shrink-0" aria-hidden />
      )}
      <span className="min-w-0 flex-1 text-center leading-snug">
        {workshopPrimaryCtaLabel(to, from)}
      </span>
      {!pending && !isCancel ? (
        <ArrowRightIcon
          className="size-4 shrink-0 opacity-75"
          aria-hidden
        />
      ) : null}
    </Button>
  );
}

export function WorkshopStatusSecondaryButton({
  status,
  pending,
  onClick,
}: {
  status: WorkshopJobStatusDTO;
  pending?: boolean;
  onClick: () => void;
}) {
  const Icon = workshopStatusActionIcon(status);
  const isCancel = status === "cancelled";

  return (
    <Button
      type="button"
      size="default"
      className={cn(
        "h-11 min-w-28 gap-2 touch-manipulation",
        !isCancel && workshopStatusSecondaryButtonClass(status),
      )}
      variant={isCancel ? "destructive" : "outline"}
      disabled={pending}
      onClick={onClick}
    >
      {pending ? (
        <Loader2Icon className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Icon className="size-4 shrink-0" aria-hidden />
      )}
      {WORKSHOP_STATUS_LABELS[status]}
    </Button>
  );
}
